import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "./UploadNewFile.scss";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifSelect,
  MotifOption,
  MotifFormField,
} from "@ey-xd/motif-react";
import { CloudUpload } from "../../assets/Icons/CloudUpload";
import { FileChecksModal } from "./FileChecksModal";
import { DeleteIcon } from "../../assets/Icons/DeleteIcon";
import { CheckCircle } from "../../assets/Icons/CheckCircle";
import { FILE_UPLOAD, PLACEHOLDERS } from "../../util/constants";
import WarningTriangle from "../../assets/Icons/WarningTriangle";
import { FilesService } from "../../api/services/files.service";

// Allowed file extensions
const ALLOWED_EXTENSIONS = ["xlsx", "xlsm", "xls","xlsb", "txt", "csv"];
const MIN_FILE_SIZE = 1024;
const FILE_TYPE_OPTIONS = [
  "BSEG",
  "BKPF",
  "GLT0",
  "T001",
  "SKAT",
  "T003T",
  "USR02",
  "V_USERNAME",
  "SKB1",
  "FAGLFLEXA",
  "FAGLFLEXT",
  "ACDOCA",
  "JE",
  "TB",
  "COA",
  "Dimension Table",
  "Fx",
  "Other",
];

// Bypass Review Mapping applies only to files outside these types
const REVIEW_MAPPING_FILE_TYPES = ["JE", "TB", "COA"];

// Alternate file-type combinations that satisfy the JE/TB mandatory requirement per year
const JE_EQUIVALENT_GROUPS = [
  ["JE"],
  ["BSEG", "BKPF"],
  ["FAGLFLEXA", "BKPF"],
  ["ACDOCA", "BKPF"],
];
const TB_EQUIVALENT_GROUPS = [["TB"], ["GLT0"], ["FAGLFLEXT"]];

const getYearFileTypes = (uploadFiles, existingFiles) =>
  new Set([
    ...uploadFiles.map((f) => f.fileType),
    ...existingFiles.map((f) => f.fileType),
  ]);

const satisfiesAnyFileTypeGroup = (fileTypes, groups) =>
  groups.some((group) => group.every((type) => fileTypes.has(type)));

// Blob storage groups every SAP source table under a single "SAP" folder
const BLOB_FOLDER_BY_FILE_TYPE = {
  JE: "JE",
  TB: "TB",
  COA: "COA",
  Other: "Other",
};

const getBlobFolderForFileType = (fileType) =>
  BLOB_FOLDER_BY_FILE_TYPE[fileType] || "SAP";

const normalizeFinancialYear = (value) => {
  const normalized = String(value || "CY").trim().toUpperCase();
  return normalized.includes("PY") || normalized.includes("PREVIOUS")
    ? "PY"
    : "CY";
};

const normalizeFileName = (value) => String(value || "").trim().toLowerCase();

const isInvalidSasTokenError = (error) => {
  if (error?.status === 401) return true;
  if (error?.status !== 403) return false;

  return (
    /AuthenticationFailed|InvalidAuthenticationInfo|SignatureNotValid/i.test(
      error?.azureCode || "",
    ) ||
    /(?:sas|signature|token).{0,40}(?:invalid|expired|not valid)|(?:invalid|expired).{0,40}(?:sas|signature|token)/i.test(
      error?.azureMessage || error?.message || "",
    )
  );
};


const FILE_TYPE_FILENAME_PATTERNS = [
  ["V_USERNAME", /(?:^|[^A-Z0-9])V_USERNAME(?:[^A-Z0-9]|$)/i],
  ["FAGLFLEXA", /(?:^|[^A-Z0-9])FAGLFLEXA(?:[^A-Z0-9]|$)/i],
  ["FAGLFLEXT", /(?:^|[^A-Z0-9])FAGLFLEXT(?:[^A-Z0-9]|$)/i],
  ["Dimension Table", /(?:^|[^A-Z0-9])DIMENSION[\s_-]+TABLE(?:[^A-Z0-9]|$)/i],
  ...[
    "BSEG",
    "BKPF",
    "GLT0",
    "T001",
    "SKAT",
    "T003T",
    "USR02",
    "SKB1",
    "ACDOCA",
    "JE",
    "TB",
    "COA",
    "Fx",
  ].map((fileType) => [
    fileType,
    new RegExp(`(?:^|[^A-Z0-9])${fileType}(?:[^A-Z0-9]|$)`, "i"),
  ]),
  // Descriptive names; explicit codes above take precedence.
  ["JE", /JOURNAL/i],
  ["TB", /TRIAL/i],
  ["COA", /CHART?/i],
  // Must stay after GLT0 so GLT0 files keep their own type.
  ["JE", /(?:^|[^A-Z0-9])GL(?:[^A-Z0-9]|$)/i],
];

const getFileTypeFromFileName = (fileName = "") => {
  const nameWithoutExtension = String(fileName).replace(/\.[^.]+$/, "");
  return (
    FILE_TYPE_FILENAME_PATTERNS.find(([, pattern]) =>
      pattern.test(nameWithoutExtension),
    )?.[0] || "Other"
  );
};

// Unreadable/problematic file signatures
const UNREADABLE_SIGNATURES = [
  /^\x00{10,}/, // Null bytes at start
  /^\xFF\xFE/, // Corrupted Unicode BOM
];

const CHUNK_SIZE = 16 * 1024 * 1024;
const UPLOAD_STATE_KEY = "axon_upload_state";

// Validation status constants
const VALIDATION_STATUS = {
  VALIDATING: "Validating",
  ALL_OK: "All ok",
  ERROR: "Error",
};

const truncateTooltipText = (text, maxLength = 20) => {
  if (!text) return "";
  return text.length > maxLength
    ? `${text.slice(0, maxLength)}...`
    : text;
};

const filterDuplicateFilesForPeriod = (
  files,
  existingFiles,
  selectedFinancialYear,
) => {
  const selectedYear = normalizeFinancialYear(selectedFinancialYear);
  const existingNames = new Set(
    existingFiles
      .filter(
        (file) => normalizeFinancialYear(file.financialYear) === selectedYear,
      )
      .map((file) => normalizeFileName(file.name)),
  );
  let hasDuplicates = false;

  const uniqueFiles = files.filter((file) => {
    const normalizedName = normalizeFileName(file.name);
    if (existingNames.has(normalizedName)) {
      hasDuplicates = true;
      return false;
    }

    existingNames.add(normalizedName);
    return true;
  });

  return { uniqueFiles, hasDuplicates };
};

// Helper function to get user-friendly error message based on error content
const getUserFriendlyErrorMessage = (error) => {
  const errorString = (error?.message || error || "").toString();
  
  // Check for 403 (Forbidden/Access Denied) errors
  if (errorString.includes("403") || errorString.toLowerCase().includes("forbidden") || errorString.toLowerCase().includes("access denied")) {
    return "Access denied. Please connect to the EY network and try again.";
  }
  
  // Check for 500 (Internal Server Error) or other 5xx errors
  if (errorString.includes("500") || errorString.includes("502") || errorString.includes("503") || errorString.includes("504") || errorString.toLowerCase().includes("internal server error")) {
    return "An unexpected error occurred. Please try after sometime.";
  }
  
  // Return original error message for other cases
  return errorString || "Unknown error";
};

const BLOB_UPLOAD_ERROR_MESSAGE =
  "An unexpected error occured. Please try again later.";

// Modal component for uploading new files with type selection
// export const UploadNewFile = ({ show, onClose }) => {
export const UploadNewFile = ({ show, onClose, onDataReceivedFlowChange }) => {
  const { orderId } = useParams();
  const user = useSelector((state) => state.user?.user);
  const currentOrder = useSelector((state) => state.orders?.currentOrder);
  
  // Get the actual orderId (requestId) from the order's headerSection
  const actualOrderId = currentOrder?.[0]?.headerSection?.[0]?.requestId || orderId;
  
  // Get axonId for blob folder structure
  const axonId = currentOrder?.[0]?.headerSection?.[0]?.axonId || actualOrderId;

  // Helper function to get countryId from order details pagefields
  const getCountryIdFromOrderDetails = () => {
    if (!Array.isArray(currentOrder)) return null;
    
    // Find the countryId field (case-insensitive)
    const countryField = currentOrder?.[0]?.pagefields?.find((field) => {
      const fieldName = String(field?.field || "").toLowerCase();
      return fieldName === "countryid" || fieldName === "country";
    });
    
    // Return fieldValueId as a number, fallback to null
    if (countryField?.fieldValueId !== undefined && countryField?.fieldValueId !== null) {
      return parseInt(countryField.fieldValueId, 10) || null;
    }
    
    return null;
  };
  
  const [financialYear, setFinancialYear] = useState("CY");
  const [showFileChecksModal, setShowFileChecksModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [validationResult, setValidationResult] = useState({
    issueCount: 0,
    message: "",
    isSuccess: true,
  });
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [bypassReviewMapping, setBypassReviewMapping] = useState(false);
  const canBypassReviewMapping = uploadedFiles.some(
    (file) => !REVIEW_MAPPING_FILE_TYPES.includes(file.fileType),
  );

  useEffect(() => {
    if (!canBypassReviewMapping) setBypassReviewMapping(false);
  }, [canBypassReviewMapping]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { fileId: { percent: 0-100, status: 'uploading'|'complete'|'failed'|'paused' } }
  const [existingBackendFiles, setExistingBackendFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const abortControllerRef = useRef({});
  const isInitialMount = useRef(true);

  // Helper function to get fresh SAS URL from the API
  const getAzureBlobSasUrl = async () => {
    const { sasUrl } = await FilesService.getSasToken();
    return sasUrl;
  };

  // Check for pending uploads on modal open and show their progress
  useEffect(() => {
    if (show && axonId) {
      try {
        const savedStates = JSON.parse(localStorage.getItem(UPLOAD_STATE_KEY) || '{}');
        const pendingUploads = Object.entries(savedStates).filter(
          ([_, state]) => state.axonId === axonId
        );

        // Show progress for any pending uploads
        if (pendingUploads.length > 0) {
          const progressState = {};
          pendingUploads.forEach(([fileId, state]) => {
            const progress = Math.round((state.lastCompletedBlock / state.totalChunks) * 100);
            progressState[fileId] = { percent: progress, status: 'paused' };
          });
          setUploadProgress(progressState);
        }
      } catch (e) {
        console.error('Failed to load pending uploads:', e);
      }
    }
  }, [show, axonId]);

  // Fetch existing files from backend when modal opens
  useEffect(() => {
    const fetchExistingFiles = async () => {
      if (show && actualOrderId) {
        try {
          const response = await FilesService.getALLFiles(actualOrderId);
          // Extract file names from the response - handle various response structures
          const rawFiles = response?.data?.files || response?.files || response?.data || response || [];
          // Ensure it's always an array
          const files = Array.isArray(rawFiles) ? rawFiles : [];
          setExistingBackendFiles(files);
        } catch (error) {
          console.error('Failed to fetch existing files:', error);
          setExistingBackendFiles([]);
        }
      }
    };

    fetchExistingFiles();
  }, [show, actualOrderId]);

  // Validates file type extension
  const validateFileType = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    return ALLOWED_EXTENSIONS.includes(extension);
  };

  // Checks for potentially encrypted content in file
  const checkForEncryptedContent = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        resolve(/EncryptedPackage/.test(content));
      };
      reader.onerror = () => resolve(false);
      // Read first 1KB to check for encryption signatures
      reader.readAsText(file.slice(0, 1024));
    });
  };

  // Performs all validations on a file
  const validateFile = async (file, selectedFileType, existingFiles = [], backendFiles = [], selectedFinancialYear = "") => {
    const errors = [];

    // Ensure backendFiles is always an array
    const safeBackendFiles = Array.isArray(backendFiles) ? backendFiles : [];

    // Check for duplicate file name in current upload session (only within same period)
    const selectedYear = normalizeFinancialYear(selectedFinancialYear);
    const normalizedFileName = normalizeFileName(file.name);
    const isDuplicateInSession = existingFiles.some(
      (existingFile) =>
        normalizeFileName(existingFile.name) === normalizedFileName &&
        normalizeFinancialYear(existingFile.financialYear) === selectedYear,
    );
    if (isDuplicateInSession) {
      errors.push(`File already exists in ${selectedFinancialYear} period. Please rename or replace the file.`);
    }

    // Check for duplicate file name in backend (already uploaded files) - only within same period
    // Skip files that are deleted or replaced - user can re-upload those
    const isDuplicateInBackend = safeBackendFiles.some(
      (backendFile) => {
        // Check if file is deleted or replaced - allow re-upload in those cases
        const fileStatus = (backendFile.status || backendFile.fileStatus || '').toLowerCase();
        if (fileStatus === 'deleted' || fileStatus === 'replaced') {
          return false;
        }
        // Check if file is in the same financial period
        const backendFilePeriod =
          backendFile.financialYear ||
          backendFile.financial_year ||
          backendFile.period;
        if (normalizeFinancialYear(backendFilePeriod) !== selectedYear) {
          return false;
        }
        // Check multiple possible field names for the file name
        const backendFileName = backendFile.fileName || backendFile.name || backendFile.file_name || backendFile.originalName || backendFile.title || '';
        return normalizeFileName(backendFileName) === normalizedFileName;
      }
    );
    if (isDuplicateInBackend) {
      errors.push(`File already exists in ${selectedFinancialYear} period. Please rename or delete the existing file first.`);
    }

    // Validate file type extension
    if (!validateFileType(file.name)) {
      errors.push("Invalid file type.");
    }

    // Every uploaded file must meet the minimum size requirement.
    if (file.size < MIN_FILE_SIZE) {
      errors.push("File must be at least 1 KB");
    }

    // Check for encrypted/password-protected content
    const isEncrypted = await checkForEncryptedContent(file);
    if (isEncrypted) {
      errors.push("Encrypted file detected. Please remove password and re-upload.");
    }

    // Check for unreadable file format
    const isUnreadable = await checkForUnreadableFormat(file);
    if (isUnreadable) {
      errors.push("Upload failed due to missing files, unreadable format, or decryption error");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Checks for unreadable file format
  const checkForUnreadableFormat = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const isUnreadable = UNREADABLE_SIGNATURES.some((pattern) =>
          pattern.test(content),
        );
        resolve(isUnreadable);
      };
      reader.onerror = () => resolve(true); // If we can't read it, it's unreadable
      // Read first 1KB to check for unreadable signatures
      reader.readAsText(file.slice(0, 1024));
    });
  };

  // useEffect to sync validation state with parent component and update validation message
  useEffect(() => {
    // Skip initial mount to avoid unnecessary updates
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const filesWithErrors = uploadedFiles.filter(
      (f) => f.validationErrors && f.validationErrors.length > 0,
    );
    const issueCount = filesWithErrors.reduce(
      (acc, f) => acc + f.validationErrors.length,
      0,
    );

    // Update local validation result
    if (issueCount > 0) {
      setValidationResult({
        issueCount,
        message: `${issueCount} Issue${issueCount > 1 ? "s" : ""} identified. Please fix before continuing.`,
        isSuccess: false,
      });
      onDataReceivedFlowChange?.({
        fileValidationStatus: "idle",
        fileMappingStatus: "idle",
        dataValidationStatus: "idle",
      });
    } else if (uploadedFiles.length > 0) {
      setValidationResult({
        issueCount: 0,
        message: "0 Issues identified. File Validation Successful.",
        isSuccess: true,
      });
      onDataReceivedFlowChange?.({
        fileValidationStatus: "done",
        fileMappingStatus: "idle",
        dataValidationStatus: "idle",
      });
    } else {
      setValidationResult({ issueCount: 0, message: "", isSuccess: true });
      onDataReceivedFlowChange?.({
        fileValidationStatus: "idle",
        fileMappingStatus: "idle",
        dataValidationStatus: "idle",
      });
    }
  }, [uploadedFiles, onDataReceivedFlowChange]);

  // Handles select all checkbox
  const handleSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        setSelectedFiles(new Set(uploadedFiles.map((f) => f.id)));
      } else {
        setSelectedFiles(new Set());
      }
    },
    [uploadedFiles],
  );

  // Handles individual file checkbox selection
  const handleSelectFile = useCallback((fileId) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  }, []);

  const handleRowFileTypeChange = (fileId, selectedFileType) => {
    setUploadedFiles((prev) =>
      prev.map((file) =>
        file.id === fileId ? { ...file, fileType: selectedFileType } : file,
      ),
    );
  };

  // Check if all files are selected
  const isAllSelected =
    uploadedFiles.length > 0 && selectedFiles.size === uploadedFiles.length;
  const isIndeterminate =
    selectedFiles.size > 0 && selectedFiles.size < uploadedFiles.length;

  // Processes selected files: shows in table immediately, then validates and extracts headers
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const { uniqueFiles, hasDuplicates } = filterDuplicateFilesForPeriod(
      files,
      uploadedFiles,
      financialYear,
    );
    setUploadError(
      hasDuplicates
        ? `File already exists in ${financialYear} period. Please rename or replace the file.`
        : null,
    );

    if (uniqueFiles.length === 0) {
      e.target.value = "";
      return;
    }

    setIsProcessing(true);

    // Process each file
    for (const file of uniqueFiles) {
      const fileId = Date.now() + Math.random();
      const extension = file.name.split(".").pop().toLowerCase();
      const detectedFileType = getFileTypeFromFileName(file.name);

      // Immediately add file to the table with "Validating" status
      // Store the current fileType and financialYear with the file
      const initialFileEntry = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        extension,
        file: file, // Store the actual File object for upload
        fileType: detectedFileType,
        financialYear: financialYear, // Store selected financial year (CY or PY)
        validationStatus: VALIDATION_STATUS.VALIDATING,
        validationErrors: [],
        isValid: true,
        isProcessing: true,
      };

      // Get current files for duplicate check before adding new file
      const currentFiles = [...uploadedFiles];
      
      setUploadedFiles((prev) => [...prev, initialFileEntry]);

      const validation = await validateFile(file, detectedFileType, currentFiles, existingBackendFiles, financialYear);
      console.log(`[${file.name}] Validation result:`, validation);

      // Update the file entry with final validation results
      setUploadedFiles((prev) => {
        const newFiles = prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                validationStatus: validation.isValid
                  ? VALIDATION_STATUS.ALL_OK
                  : VALIDATION_STATUS.ERROR,
                validationErrors: validation.errors,
                isValid: validation.isValid,
                isProcessing: false,
              }
            : f,
        );
        return newFiles;
      });
    }
    setIsProcessing(false);
    e.target.value = "";
  };

  // Removes uploaded file from the list
  const handleRemoveFile = (fileId) => {
    removeUploadedFiles([fileId]);
  };

  const removeUploadedFiles = (fileIds) => {
    const fileIdsToRemove = new Set(fileIds);

    fileIdsToRemove.forEach((fileId) => {
      abortControllerRef.current[fileId]?.abort();
      clearUploadState(fileId);
    });

    setUploadProgress((previousProgress) => {
      const nextProgress = { ...previousProgress };
      fileIdsToRemove.forEach((fileId) => delete nextProgress[fileId]);
      return nextProgress;
    });

    const remainingFiles = uploadedFiles.filter(
      (file) => !fileIdsToRemove.has(file.id),
    );
    setUploadedFiles(remainingFiles);
    // Clear error messages when all files are deleted.
    if (remainingFiles.length === 0) {
      setUploadError(null);
    }

    setSelectedFiles((previousSelection) =>
      new Set(
        Array.from(previousSelection).filter(
          (fileId) => !fileIdsToRemove.has(fileId),
        ),
      ),
    );
  };

  const handleDeleteAll = () => {
    removeUploadedFiles(uploadedFiles.map((file) => file.id));
  };

  const handleDeleteSelectedFiles = () => {
    removeUploadedFiles(Array.from(selectedFiles));
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;

    const { uniqueFiles, hasDuplicates } = filterDuplicateFilesForPeriod(
      files,
      uploadedFiles,
      financialYear,
    );
    setUploadError(
      hasDuplicates
        ? `File already exists in ${financialYear} period. Please rename or replace the file.`
        : null,
    );

    if (uniqueFiles.length === 0) {
      return;
    }

    setIsProcessing(true);

    // Process each dropped file
    for (const file of uniqueFiles) {
      const fileId = Date.now() + Math.random();
      const extension = file.name.split(".").pop().toLowerCase();
      const detectedFileType = getFileTypeFromFileName(file.name);

      // Immediately add file to the table with "Validating" status
      const initialFileEntry = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        extension,
        file: file,
        fileType: detectedFileType,
        financialYear: financialYear,
        validationStatus: VALIDATION_STATUS.VALIDATING,
        validationErrors: [],
        isValid: true,
        isProcessing: true,
      };

      const currentFiles = [...uploadedFiles];
      setUploadedFiles((prev) => [...prev, initialFileEntry]);

      // Run validation
      const validation = await validateFile(file, detectedFileType, currentFiles, existingBackendFiles, financialYear);
      console.log(`[${file.name}] Validation result:`, validation);

      // Update the file entry with final validation results
      setUploadedFiles((prev) => {
        const newFiles = prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                validationStatus: validation.isValid
                  ? VALIDATION_STATUS.ALL_OK
                  : VALIDATION_STATUS.ERROR,
                validationErrors: validation.errors,
                isValid: validation.isValid,
                isProcessing: false,
              }
            : f,
        );
        return newFiles;
      });
    }
    setIsProcessing(false);
  };

  // Generates a unique block ID for Azure Block Blob
  const createUploadSessionId = () =>
    Math.random().toString(36).slice(2, 10).padEnd(8, "0");

  const generateBlockId = (index, uploadSessionId = "") => {
    const blockIdString = `${uploadSessionId}${String(index).padStart(6, "0")}`;
    return btoa(blockIdString);
  };

  // Saves upload state to localStorage for resumability
  const saveUploadState = (fileId, state) => {
    try {
      const existingState = JSON.parse(localStorage.getItem(UPLOAD_STATE_KEY) || '{}');
      existingState[fileId] = { ...state, axonId, timestamp: Date.now() };
      localStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify(existingState));
    } catch (e) {
      console.error('Failed to save upload state:', e);
    }
  };

  // Clears upload state from localStorage
  const clearUploadState = (fileId) => {
    try {
      const existingState = JSON.parse(localStorage.getItem(UPLOAD_STATE_KEY) || '{}');
      delete existingState[fileId];
      localStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify(existingState));
    } catch (e) {
      console.error('Failed to clear upload state:', e);
    }
  };

  // Gets saved upload state from localStorage
  const getSavedUploadState = (fileId) => {
    try {
      const existingState = JSON.parse(localStorage.getItem(UPLOAD_STATE_KEY) || '{}');
      return existingState[fileId] || null;
    } catch (e) {
      return null;
    }
  };

  // Uploads a single block to Azure Blob Storage
  const uploadBlock = async (blobUrl, sasToken, blockId, chunk, signal) => {
    // Append comp=block and blockid parameters for Put Block operation
    const blockUrl = `${blobUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;
    
    const response = await fetch(blockUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/octet-stream",
        "Content-Length": chunk.size.toString(),
        "x-ms-version": "2020-10-02",
      },
      body: chunk,
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      const azureCode = errorText.match(/<Code>\s*([^<]+)\s*<\/Code>/i)?.[1];
      const azureMessage = errorText.match(/<Message>\s*([\s\S]*?)\s*<\/Message>/i)?.[1];
      const error = new Error(`Block upload failed: ${response.status} - ${errorText}`);
      error.status = response.status;
      error.azureCode = azureCode || "";
      error.azureMessage = azureMessage || errorText;
      throw error;
    }

    return true;
  };

  // Commits all uploaded blocks to finalize the blob
  const commitBlocks = async (blobUrl, sasToken, blockIds, contentType) => {
    const commitUrl = `${blobUrl}&comp=blocklist`;
    
    // Create the block list XML
    const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
<BlockList>
${blockIds.map(id => `  <Latest>${id}</Latest>`).join('\n')}
</BlockList>`;

    const response = await fetch(commitUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/xml",
        "x-ms-blob-content-type": contentType || "application/octet-stream",
      },
      body: blockListXml,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Commit blocks failed: ${response.status} - ${errorText}`);
    }

    return true;
  };

  // Uploads a single file to Azure Blob Storage using chunked upload
  const uploadFileToBlob = async (
    fileEntry,
    resumeFromBlock = 0,
    { restartFromFirstBlock = false, allowInvalidBlockListRetry = true } = {},
  ) => {
    const file = fileEntry.file;
    if (!file) {
      throw new Error("File object not found");
    }

    if (!axonId) {
      throw new Error("Axon ID not found");
    }

    // Get period (CY/PY) and blob folder for the file entry
    const period = fileEntry.financialYear || "CY";
    const fileTypeFolder = getBlobFolderForFileType(fileEntry.fileType || "Other");

    // Check for saved state to resume
    const storedUploadState = restartFromFirstBlock
      ? null
      : getSavedUploadState(fileEntry.id);
    const hasIncompatibleUploadState = Boolean(
      storedUploadState &&
        (storedUploadState.chunkSize !== CHUNK_SIZE ||
          storedUploadState.fileSize !== file.size),
    );
    if (hasIncompatibleUploadState) clearUploadState(fileEntry.id);
    const savedState = hasIncompatibleUploadState ? null : storedUploadState;
    let blobName = savedState?.blobName;
    let blobPath = savedState?.blobPath;
    let uploadedBlocks = savedState?.uploadedBlocks || [];
    let startBlock = savedState?.lastCompletedBlock ??
      (storedUploadState ? 0 : resumeFromBlock);
    const uploadSessionId = restartFromFirstBlock
      ? createUploadSessionId()
      : savedState?.uploadSessionId || (savedState ? "" : createUploadSessionId());

    // Generate a unique blob name and path if not resuming
    if (!blobName) {
      blobName = file.name;
      // Folder structure: axonId/period/fileType/filename
      blobPath = `${axonId}/${period}/${fileTypeFolder}/${blobName}`;
      // blobPath=blobName
    }
    
    // Get fresh SAS token and construct the full URL for the blob
    const buildBlobUrl = (sasUrl) => {
      const [containerUrl, sasToken] = sasUrl.includes("?")
        ? [sasUrl.split("?")[0], `?${sasUrl.split("?").slice(1).join("?")}`]
        : [sasUrl, ""];

      return {
        containerUrl,
        sasToken,
        blobUrl: `${containerUrl}/DataTriage/${blobPath}${sasToken}`,
      };
    };
    let { containerUrl, sasToken, blobUrl } = buildBlobUrl(
      await getAzureBlobSasUrl(),
    );

    // Calculate total chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const abortController = new AbortController();
    abortControllerRef.current[fileEntry.id] = abortController;

    try {
      // Upload each chunk
      for (let i = startBlock; i < totalChunks; i++) {
        // Check if upload was aborted
        if (abortController.signal.aborted) {
          throw new Error("Upload cancelled");
        }

        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const blockId = generateBlockId(i, uploadSessionId);

        try {
          await uploadBlock(blobUrl, sasToken, blockId, chunk, abortController.signal);
        } catch (error) {
          if (!isInvalidSasTokenError(error)) throw error;

          ({ containerUrl, sasToken, blobUrl } = buildBlobUrl(
            await getAzureBlobSasUrl(),
          ));
          await uploadBlock(blobUrl, sasToken, blockId, chunk, abortController.signal);
        }
        
        uploadedBlocks.push(blockId);
        const progress = Math.round(((i + 1) / totalChunks) * 100);

        // Update progress
        setUploadProgress((prev) => ({
          ...prev,
          [fileEntry.id]: { percent: progress, status: 'uploading' },
        }));

        // Save state for resumability
        saveUploadState(fileEntry.id, {
          blobName,
          blobPath,
          uploadedBlocks,
          uploadSessionId,
          lastCompletedBlock: i + 1,
          totalChunks,
          fileName: file.name,
          fileSize: file.size,
          chunkSize: CHUNK_SIZE,
          period,
          fileType: fileTypeFolder,
        });
      }

      // Commit all blocks to finalize the blob
      try {
        const completeBlockList = Array.from(
          { length: totalChunks },
          (_, index) => generateBlockId(index, uploadSessionId),
        );
        await commitBlocks(blobUrl, sasToken, completeBlockList, file.type);
      } catch (error) {
        if (
          allowInvalidBlockListRetry &&
          /InvalidBlockList/i.test(error?.message || "")
        ) {
          clearUploadState(fileEntry.id);
          return await uploadFileToBlob(fileEntry, 0, {
            restartFromFirstBlock: true,
            allowInvalidBlockListRetry: false,
          });
        }

        throw error;
      }

      // Clear saved state on successful completion
      clearUploadState(fileEntry.id);

      return {
        blobName,
        blobPath,
        blobUrl: `${containerUrl}/${blobPath}`,
        success: true,
      };
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'Upload cancelled') {
        // Upload was paused/cancelled, state is already saved
        setUploadProgress((prev) => ({
          ...prev,
          [fileEntry.id]: { ...prev[fileEntry.id], status: 'paused' },
        }));
        throw error;
      }

      // Blob service responses can contain XML, request IDs, and other raw details.
      // Keep those out of user-facing errors while preserving the original in logs.
      console.error(`Blob upload failed for ${fileEntry.name}:`, error);
      const safeError = new Error(BLOB_UPLOAD_ERROR_MESSAGE);
      safeError.isBlobUploadError = true;
      throw safeError;
    } finally {
      delete abortControllerRef.current[fileEntry.id];
    }
  };

  // Uploads all valid files to Azure Blob Storage with chunk-based upload
  const uploadAllFilesToBlob = async () => {
    const validFiles = uploadedFiles.filter((f) => f.isValid && f.file);
    
    if (validFiles.length === 0) {
      throw new Error("No valid files to upload");
    }

    const results = [];
    const errors = [];

    for (const fileEntry of validFiles) {
      try {
        // Check if there's a saved state to resume from
        const savedState = getSavedUploadState(fileEntry.id);
        const startBlock = savedState?.lastCompletedBlock || 0;
        const initialProgress = savedState ? Math.round((savedState.lastCompletedBlock / savedState.totalChunks) * 100) : 0;

        // Initialize progress for this file
        setUploadProgress((prev) => ({
          ...prev,
          [fileEntry.id]: { percent: initialProgress, status: 'uploading' },
        }));

        const result = await uploadFileToBlob(fileEntry, startBlock);
        results.push({ ...result, fileId: fileEntry.id, fileName: fileEntry.name });

        // Update progress as complete
        setUploadProgress((prev) => ({
          ...prev,
          [fileEntry.id]: { percent: 100, status: 'complete' },
        }));
      } catch (error) {
        // Don't count paused uploads as errors
        if (error.message === 'Upload cancelled' || error.name === 'AbortError') {
          continue;
        }
        
        console.error(`Failed to upload ${fileEntry.name}:`, error);
        errors.push({ fileId: fileEntry.id, fileName: fileEntry.name, error: error.message });

        // Update progress as failed
        setUploadProgress((prev) => ({
          ...prev,
          [fileEntry.id]: { ...prev[fileEntry.id], status: 'failed' },
        }));
      }
    }

    return { results, errors };
  };

  // Cancel/pause a specific file upload
  const cancelFileUpload = (fileId) => {
    if (abortControllerRef.current[fileId]) {
      abortControllerRef.current[fileId].abort();
    }
  };

  // Resume upload for files with saved state
  const resumePendingUploads = useCallback(async () => {
    try {
      const savedStates = JSON.parse(localStorage.getItem(UPLOAD_STATE_KEY) || '{}');
      const pendingUploads = Object.entries(savedStates).filter(
        ([_, state]) => state.axonId === axonId
      );

      if (pendingUploads.length > 0) {
        setIsUploading(true);
        
        for (const [fileId, state] of pendingUploads) {
          // Find matching file in uploadedFiles or create a placeholder
          const fileEntry = uploadedFiles.find(f => f.id === fileId);
          if (fileEntry && fileEntry.file) {
            const initialProgress = Math.round((state.lastCompletedBlock / state.totalChunks) * 100);
            setUploadProgress((prev) => ({
              ...prev,
              [fileId]: { percent: initialProgress, status: 'uploading' },
            }));

            try {
              await uploadFileToBlob(fileEntry, state.lastCompletedBlock);
              setUploadProgress((prev) => ({
                ...prev,
                [fileId]: { percent: 100, status: 'complete' },
              }));
            } catch (error) {
              if (error.message !== 'Upload cancelled') {
                console.error(`Failed to resume upload for ${state.fileName}:`, error);
              }
            }
          }
        }
        
        setIsUploading(false);
      }
    } catch (e) {
      console.error('Failed to resume uploads:', e);
      setIsUploading(false);
    }
  }, [axonId, uploadedFiles]);

  // Validates folder-level requirements before upload
  const validateFolderRequirements = () => {
    const errors = [];
    
    // Get valid files only (exclude files with validation errors) from current upload
    const validFiles = uploadedFiles.filter((f) => f.isValid);
    
    // Ensure existingBackendFiles is an array
    const safeExistingFiles = Array.isArray(existingBackendFiles) ? existingBackendFiles : [];
    
    // Separate current upload files by financial year
    const cyFiles = validFiles.filter((f) => f.financialYear === "CY");
    const pyFiles = validFiles.filter((f) => f.financialYear === "PY");
    
    // Separate existing backend files by period (CY/PY)
    const existingCYFiles = safeExistingFiles.filter((f) => f.period === "CY");
    const existingPYFiles = safeExistingFiles.filter((f) => f.period === "PY");
    
    // Check if PY files are being uploaded but no CY files exist (neither in upload nor backend)
    const hasCYFiles = cyFiles.length > 0 || existingCYFiles.length > 0;
    if (pyFiles.length > 0 && !hasCYFiles) {
      errors.push("CY folder cannot be empty, please ensure to upload files to correct folder");
      return {
        isValid: false,
        errors,
      };
    }
    
    // Check CY folder for mandatory files (JE-equivalent and TB-equivalent)
    const cyFileTypes = getYearFileTypes(cyFiles, existingCYFiles);
    if (
      !satisfiesAnyFileTypeGroup(cyFileTypes, JE_EQUIVALENT_GROUPS) ||
      !satisfiesAnyFileTypeGroup(cyFileTypes, TB_EQUIVALENT_GROUPS)
    ) {
      errors.push("CY Folder: Mandatory files cannot be Missing. Please upload all mandatory files before proceeding");
    }
    
    // Check PY folder for mandatory files if any PY files exist (upload or backend)
    const hasPYFiles = pyFiles.length > 0 || existingPYFiles.length > 0;
    if (hasPYFiles) {
      const pyFileTypes = getYearFileTypes(pyFiles, existingPYFiles);
      if (
        !satisfiesAnyFileTypeGroup(pyFileTypes, JE_EQUIVALENT_GROUPS) ||
        !satisfiesAnyFileTypeGroup(pyFileTypes, TB_EQUIVALENT_GROUPS)
      ) {
        errors.push("PY Folder: Mandatory files cannot be Missing. Please upload all mandatory files before proceeding");
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Handles Continue button click - validates files and calls upload API
  const handleContinue = async () => {
    if (uploadedFiles.length === 0 || !validationResult.isSuccess) {
      setUploadError("Please add a valid file before continuing");
      return;
    }

    // Perform folder-level validation before upload
    const folderValidation = validateFolderRequirements();
    if (!folderValidation.isValid) {
      setUploadError(folderValidation.errors.join(" "));
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Get valid files for blob upload
      const validFiles = uploadedFiles.filter((f) => f.isValid && f.file);
      const failedUploads = [];
      const successfulUploads = [];

      if (validFiles.length > 0) {
        // Initialize progress for all files
        const initialProgress = {};
        validFiles.forEach((file) => {
          const savedState = getSavedUploadState(file.id);
          const percent = savedState 
            ? Math.round((savedState.lastCompletedBlock / savedState.totalChunks) * 100) 
            : 0;
          initialProgress[file.id] = { percent, status: 'uploading' };
        });
        setUploadProgress(initialProgress);

        // Upload all files to blob in parallel; the backend API is called once afterwards
        const uploadPromises = validFiles.map(async (fileEntry) => {
          try {
            const savedState = getSavedUploadState(fileEntry.id);
            const startBlock = savedState?.lastCompletedBlock || 0;
            
            const blobResult = await uploadFileToBlob(fileEntry, startBlock);
            
            // Update progress as complete
            setUploadProgress((prev) => ({
              ...prev,
              [fileEntry.id]: { percent: 100, status: 'complete' },
            }));
            
            return { 
              success: true,
              fileId: fileEntry.id, 
              fileName: fileEntry.name,
              fileEntry,
              ...blobResult 
            };
          } catch (error) {
            // Don't count paused uploads as errors
            if (error.message === 'Upload cancelled' || error.name === 'AbortError') {
              return { success: false, cancelled: true, fileId: fileEntry.id, fileName: fileEntry.name };
            }
            
            console.error(`Failed to upload ${fileEntry.name}:`, error);
            
            // Update progress as failed
            setUploadProgress((prev) => ({
              ...prev,
              [fileEntry.id]: { ...prev[fileEntry.id], status: 'failed' },
            }));
            
            return { 
              success: false,
              fileId: fileEntry.id, 
              fileName: fileEntry.name, 
              error: error.message,
              isBlobUploadError: Boolean(error.isBlobUploadError),
            };
          }
        });

        // Wait for all parallel uploads to complete
        const uploadResults = await Promise.all(uploadPromises);
        
        // Separate successful and failed uploads
        uploadResults.forEach((result) => {
          if (result.success) {
            successfulUploads.push(result);
          } else if (!result.cancelled) {
            failedUploads.push(result);
          }
        });
        
        // Check for any failed uploads
        if (failedUploads.length > 0) {
          if (failedUploads.some((file) => file.isBlobUploadError)) {
            setUploadError(BLOB_UPLOAD_ERROR_MESSAGE);
            setIsUploading(false);
            return;
          }

          // Get the first error and check for 403/500 errors to show user-friendly message
          const firstError = failedUploads[0]?.error || "";
          const userFriendlyMessage = getUserFriendlyErrorMessage(firstError);
          
          // If it's a 403 or 500 error, show the user-friendly message directly
          if (userFriendlyMessage.includes("Access denied") || userFriendlyMessage.includes("unexpected error")) {
            setUploadError(userFriendlyMessage);
          } else {
            const errorMessages = failedUploads.map(f => `${f.fileName}: ${getUserFriendlyErrorMessage(f.error)}`).join(', ');
            setUploadError(`Some files failed to upload: ${errorMessages}`);
          }
          setIsUploading(false);
          return;
        }

        console.log("All files uploaded to blob:", successfulUploads);

        // Single backend call carrying every uploaded file
        const payload = {
          orderId: actualOrderId?.toString() || "",
          uploadedBy: user?.email || user?.mail || user?.username || "",
          bypassReviewMapping,
          files: successfulUploads.map(({ fileEntry, blobPath }) => ({
            countryId: getCountryIdFromOrderDetails() || 3,
            period: fileEntry.financialYear || financialYear,
            fileType: fileEntry.fileType || "Other",
            mappingMode: ["JE", "TB", "COA"].includes(fileEntry.fileType) ? "Standard" : "SAP",
            fileName: fileEntry.name,
            filePath: blobPath || "",
          })),
        };
        if (payload.files.length > 0) {
          const apiResponse = await FilesService.uploadFiles(payload);
          console.log("Backend upload response:", apiResponse);
        }
        
        // Clear all file-related states after successful upload
        setUploadedFiles([]);
        setSelectedFiles(new Set());
        setUploadProgress({});
        setValidationResult({ issueCount: 0, message: "", isSuccess: true });
        setFinancialYear("CY");
      }

      // Proceed to file checks modal after ALL files are successfully uploaded
      setShowFileChecksModal(true);
      onClose();
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError(getUserFriendlyErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  // Closes file checks modal
  const handleFileChecksClose = useCallback(() => {
    setShowFileChecksModal(false);
  }, []);

  // Returns to upload modal from file checks
  const handleAttachFilesFromChecks = useCallback(() => {
    setShowFileChecksModal(false);
  }, []);

  return (
    <>
      <MotifModal
        show={show}
        onClose={onClose}
        size="xl"
        className="upload-new-file-modal"
      >
        <MotifModalHeader>{FILE_UPLOAD.UPLOAD_NEW_FILE}</MotifModalHeader>
        <MotifModalBody>
          <div className="upload-modal-body-content">
            {/* Left Section - Dropdowns and File Upload */}
            <div className="upload-modal-left-section">
              {/* Dropdowns */}
              <div className="upload-modal-dropdowns-section">
                <div className="upload-modal-dropdown-group">
                  <label className="upload-modal-label">
                    {FILE_UPLOAD.FINANCIAL_YEAR}
                  </label>
                  <MotifFormField>
                    <MotifSelect
                      className="upload-modal-dropdown financial-year-select"
                      value={financialYear}
                      onChange={(e) => setFinancialYear(e)}
                      placeholder={PLACEHOLDERS.SELECT}
                      visibleOptions={2}
                      filter
                    >
                      <MotifOption value="PY">
                        {FILE_UPLOAD.PREVIOUS_YEAR}
                      </MotifOption>
                      <MotifOption value="CY">
                        {FILE_UPLOAD.CURRENT_YEAR}
                      </MotifOption>
                    </MotifSelect>
                  </MotifFormField>
                </div>
              </div>

              {/* File Upload */}
              <div className="upload-modal-file-section">
                <div className="upload-modal-file-header">
                  <label className="upload-modal-label">
                    {FILE_UPLOAD.FILE_UPLOAD_TITLE}
                  </label>
                  <span className="upload-modal-note">
                    <span className="note-link"> Note: Files can be uploaded only on EY network or EY approved VPN.</span>
                  </span>
                </div>
                <input
                  type="file"
                  className="upload-modal-file-input"
                  id="file-upload-input"
                  onChange={handleFileChange}
                  multiple
                />
                <label
                  htmlFor="file-upload-input"
                  className={`upload-modal-file-dropzone ${isDragging ? "dragging" : ""}`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <p className="file-upload-icon">
                    <CloudUpload width={20} height={20} color="#a4a3b1" />
                  </p>
                  <p>
                    <b style={{ color: "#fff" }}>
                      {FILE_UPLOAD.CLICK_TO_UPLOAD}
                    </b>{" "}
                    {FILE_UPLOAD.DRAG_AND_DROP}
                  </p>
                  <p className="normal-filetype">
                    {FILE_UPLOAD.SUPPORTED_FORMATS}
                  </p>
                </label>
              </div>

              {/* File Validation Section */}
              {uploadedFiles.length > 0 && (
                <div className="upload-modal-validation-section">
                  {/* Validation Status Message */}
                  <div
                    className={`validation-status-message ${validationResult.isSuccess ? "success" : "error"}`}
                  >
                    {validationResult.isSuccess ? (
                      <CheckCircle width={16} height={16} color="#4CAF50" />
                    ) : (
                      <WarningTriangle width={16} height={16} color="#ea011d" />
                    )}
                    <span>{validationResult.message}</span>
                  </div>

                  {/* Files Table */}
                  <div className="validation-files-table">
                    <div className="validation-table-header">
                      <div className="table-col checkbox-col">
                        <input
                          type="checkbox"
                          className="table-checkbox"
                          checked={isAllSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = isIndeterminate;
                          }}
                          onChange={handleSelectAll}
                        />
                      </div>
                      {selectedFiles.size > 0 ? (
                        <>
                          <div className="table-col file-name-col selected-count">
                            {selectedFiles.size} file
                            {selectedFiles.size > 1 ? "s" : ""} selected
                          </div>
                          <div className="table-col uploaded-by-col"></div>
                          <div className="table-col year-col"></div>
                          <div className="table-col file-type-col"></div>
                          <div className="table-col status-col"></div>
                          <div className="table-col action-col">
                            <button
                              className="delete-all-btn"
                              onClick={handleDeleteSelectedFiles}
                              title="Delete selected files"
                              disabled={isProcessing}
                            >
                              <DeleteIcon
                                width={16}
                                height={16}
                                color="#737387"
                              />
                              Delete
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="table-col file-name-col">
                            File Name
                          </div>
                          
                          <div className="table-col year-col">
                            Year
                          </div>
                          <div className="table-col file-type-col">
                            File Type
                          </div>
                          <div className="table-col uploaded-by-col">
                            Uploaded By
                          </div>
                          <div className="table-col status-col">Status</div>
                          <div className="table-col action-col">
                            <button
                              className="delete-all-btn"
                              onClick={handleDeleteAll}
                              title="Delete all files"
                              disabled={isProcessing}
                            >
                              <DeleteIcon
                                width={16}
                                height={16}
                                color="#737387"
                              />
                              Delete All
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="validation-table-body">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`validation-table-row ${!file.isValid ? "has-error" : ""}`}
                        >
                          <div className="table-col checkbox-col">
                            <input
                              type="checkbox"
                              className="table-checkbox"
                              checked={selectedFiles.has(file.id)}
                              onChange={() => handleSelectFile(file.id)}
                            />
                          </div>
                          <div
                            className="table-col file-name-col"
                            title={file.name}
                          >
                            {file.name}
                          </div>
                          
                          <div className="table-col year-col">
                            {file.financialYear || "N/A"}
                          </div>
                          <div className="table-col file-type-col">
                            <MotifSelect
                              className="file-type-row-select"
                              value={file.fileType || "Other"}
                              onChange={(value) =>
                                handleRowFileTypeChange(file.id, value)
                              }
                              aria-label={`File type for ${file.name}`}
                              visibleOptions={4}
                            >
                              {FILE_TYPE_OPTIONS.map((option) => (
                                <MotifOption key={option} value={option}>
                                  {option}
                                </MotifOption>
                              ))}
                            </MotifSelect>
                          </div>
                          <div
                            className="table-col uploaded-by-col"
                            title={user?.name || "N/A"}
                          >
                            {user?.name || "N/A"}
                          </div>
                          <div className="table-col status-col">
                            {uploadProgress[file.id] ? (
                              <div className="upload-progress-container">
                                <div className="upload-progress-bar">
                                  <div 
                                    className={`upload-progress-fill ${uploadProgress[file.id].status}`}
                                    style={{ width: `${uploadProgress[file.id].percent}%` }}
                                  />
                                </div>
                                <span className={`upload-progress-text ${uploadProgress[file.id].status}`}>
                                  {uploadProgress[file.id].status === 'complete' 
                                    ? 'Completed' 
                                    : uploadProgress[file.id].status === 'failed'
                                    ? 'Failed'
                                    : uploadProgress[file.id].status === 'paused'
                                    ? `Paused ${uploadProgress[file.id].percent}%`
                                    : `${uploadProgress[file.id].percent}%`}
                                </span>
                              </div>
                            ) : (
                              <>
                                <span
                                  className={`status-badge ${file.isValid ? "success" : "error"}`}
                                >
                                  {file.validationStatus ||
                                    VALIDATION_STATUS.VALIDATING}
                                </span>
                                {file.validationErrors?.length > 0 && (
                                  <div className="validation-error-tooltip">
                                    {file.validationErrors.map((error, idx) => (
                                      <div key={idx}>
                                        {error}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="table-col action-col">
                            <button
                              className="delete-file-btn"
                              onClick={() => handleRemoveFile(file.id)}
                              title="Remove file"
                              disabled={file.isProcessing || isProcessing}
                            >
                              <DeleteIcon
                                width={16}
                                height={16}
                                color="#737387"
                              />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          {uploadError && (
            <div className="upload-modal-error">{uploadError}</div>
          )}
          <div className="upload-modal-footer-buttons">
            <label className="bypass-review-mapping-control">
              <input
                type="checkbox"
                className="table-checkbox"
                checked={bypassReviewMapping}
                disabled={!canBypassReviewMapping}
                onChange={(event) => setBypassReviewMapping(event.target.checked)}
              />
              <span>Bypass Review Mapping</span>
            </label>
            <div className="upload-modal-footer-actions">
              <button
                className="upload-modal-cancel-btn"
                onClick={onClose}
              >
                {FILE_UPLOAD.CANCEL}
              </button>
              <button
                className="upload-modal-upload-btn"
                onClick={handleContinue}
                disabled={
                  uploadedFiles.length === 0 ||
                  !validationResult.isSuccess ||
                  isProcessing ||
                  isUploading
                }
              >
                {isUploading ? "Uploading..." : FILE_UPLOAD.CONTINUE}
              </button>
            </div>
          </div>
        </MotifModalFooter>
      </MotifModal>

      <FileChecksModal
        show={showFileChecksModal}
        onClose={handleFileChecksClose}
        onAttachFiles={handleAttachFilesFromChecks}
        onReplace={handleAttachFilesFromChecks}
        onDataReceivedFlowChange={onDataReceivedFlowChange}
      />
    </>
  );
};
